// ---------------------------------------------------------------------------
// The Stock tab checkout's hand-off: seeds the Logistics delivery dialog from
// a task's stock claims the same way an inventory row's own "Order now" seeds
// it (`LogisticsPage/orderPrefill.ts`'s `buildOrderPrefill`) — but from a
// TASK's claim rather than a stock level, so there is no delivery history to
// draw a destination or note from. Both are built from what the claim and
// its task already know instead. Also the adds to orders already on their way
// the checkout performs, as ready write bodies.
//
// Pure functions — no React, no store; unit-tested in isolation.
// ---------------------------------------------------------------------------

import type { TFunction } from 'i18next';
import type { ISchedulerTask, ITaskStockClaim } from '@interfaces/IScheduler';
import type { InventoryLevel } from '@interfaces/ILogistics';
import type { IJoinWrite, IOrderHandoff, IOrderHandoffLine } from '../../LogisticsPage/orderHandoff';
import { addDays, zonedDay } from '../../LogisticsPage/logistics.format';
import type { INewLine, IPlannedJoin } from './checkoutPlan';

/** Where the delivery goes: the task's FIRST assigned zone and the floor it
 *  sits on, both by name — null when the task has no location, or its first
 *  zone is not in the catalog. */
export interface IClaimDestination {
  floorName: string;
  zoneName: string;
}

/** The task a checkout writes for, and what it knows about where it works. */
export interface ICheckoutTask {
  task: Pick<ISchedulerTask, 'id' | 'name' | 'start' | 'end'>;
  /** The schedule the task belongs to — task ids repeat across schedules, so
   *  the order's source link names both. */
  scheduleNumber: string;
  /** Every floor the task is scoped to, by catalog name in catalog order —
   *  empty for a task with no location. */
  floorNames: string[];
  destination: IClaimDestination | null;
  /** Destination fallback when the task has no resolved floor+zone. Unlike
   *  `buildOrderPrefill`, there is no delivery history to fall back on here —
   *  the site itself is the honest default rather than an invented location. */
  siteName: string;
  /** The CAD take-off storeys the task's floors map to — empty for floors
   *  that never resolved one. A "Not in the CAD" free-text claim cites none
   *  of them (`storeysFor`). */
  tz: string;
  t: TFunction;
}

export interface IClaimOrderPrefillInput extends Omit<ICheckoutTask, 'floorNames'> {
  claim: ITaskStockClaim;
  /** The amount still to source — the whole quantity with no stock on site,
   *  the shortfall with some — not the claim's raw `quantity`. */
  need: number;
  /** The material's running stock, when it has one — null with none on site,
   *  which has nothing to read a lead time or daily rate from. */
  level: InventoryLevel | null;
}

/* The note names the task and its dates, and nothing else (Niv, 2026-09-16,
   mock dispatch-note-naming.html option C): where the truck goes is the
   order's own Destination, and the floors sit under the task in the drawer.
   It used to cite "CAD take-off, storey 110, 110, L09…" — the take-off's own
   level labels, repeated once per storey that maps to the same floor. */

/** The one shape both task notes share — the create-time sentence and the
 *  join line differ only in their key pair and the params they carry, so the
 *  "is there a CAD source?" branch lives here once. */
function taskNoteLine(key: string, params: Record<string, string | number>, t: TFunction): string {
  return t(key, params);
}

/**
 * One material of the order the checkout raises.
 *
 * - **destination** — `{floor} · {zone}` of the task's first location when
 *   it resolves, else the site name.
 * - **due date** — the level's own lead time from today when there is a
 *   level (identical reasoning to `buildOrderPrefill`'s); with no lead time
 *   known, left EMPTY (ruled 2026-09-09). Guessing from the task's start
 *   seeded dates in the past for a late task, and a date the system cannot
 *   back is worse than none — the user picks it.
 * - **note** — names the task and its window, plus the CAD source (take-off,
 *   storeys joined) when one is known. Carried twice: as the one-material
 *   note, and as `taskNote`, which an order carrying several of the task's
 *   materials writes once.
 */
export function buildClaimOrderPrefill(input: IClaimOrderPrefillInput): IOrderHandoffLine {
  const { claim, need, level, task, scheduleNumber, destination, siteName, tz, t } = input;

  const today = zonedDay(new Date().toISOString(), tz);
  const leadTimeDays = level?.leadTimeDays ?? null;
  const dueDate = leadTimeDays != null ? addDays(today, leadTimeDays) : '';

  const deliveryOrder = taskNoteLine(
    'logistics.orderDialog.fromTaskNoteNoCad',
    { task: task.name, start: task.start, end: task.end },
    t,
  );

  return {
    // The need is the task's share of the line it raises (contract v13): a
    // later, larger claim measures itself against it, not against the line.
    source: { scheduleNumber, taskId: task.id, quantity: need },
    prefill: {
      itemName: claim.materialName,
      destinationName: destination === null ? siteName : `${destination.floorName} · ${destination.zoneName}`,
      // The task's own location, not a place read off a past delivery — there
      // is no order for the footnote to name.
      destinationSource: 'task',
      quantity: need,
      unit: claim.unit,
      requestedDate: today,
      dueDate,
      leadTimeDays,
      dailyRate: level?.dailyRate ?? 0,
      deliveryOrder,
      // Not a reorder line off a stock row — the TASK's sentence, which the
      // multi-material note collects once from `taskNote` instead.
      reorderNote: '',
      taskNote: deliveryOrder,
    },
  };
}

/** What the checkout hands the delivery dialog: one line per material the
 *  new order carries, the adds Create also performs, and the task's name and
 *  floors for the dialog's origin line. */
export function buildCheckoutHandoff(
  newLines: INewLine[],
  joins: IJoinWrite[],
  ctx: ICheckoutTask,
): IOrderHandoff {
  return {
    lines: newLines.map(({ claim, need, level }) =>
      buildClaimOrderPrefill({ ...ctx, claim, need, level }),
    ),
    joins,
    taskName: ctx.task.name,
    floorName: ctx.floorNames.length > 0 ? ctx.floorNames.join(', ') : null,
  };
}

/** The checkout's adds to orders already on their way, ready to post: the
 *  LINE's own spelling (joining must not rename the material on someone
 *  else's order), the amount, the task, and the note line it appends. */
export function joinWritesFor(joins: IPlannedJoin[], ctx: ICheckoutTask): IJoinWrite[] {
  const { task, scheduleNumber, t } = ctx;
  return joins.map(({ claim, order, amount }) => ({
    claimId: claim.id,
    ref: order.ref,
    body: {
      itemName: order.line.itemName,
      quantity: amount,
      unit: order.line.unit,
      source: { scheduleNumber, taskId: task.id },
      note: joinNoteLine(task, amount, order.line.unit, t),
    },
  }));
}

/**
 * The line a join appends to the order's dispatch note (ruling 2026-09-14,
 * one line per task): the task, its window, the amount it added, and the
 * CAD source when one is known — the create-time sentence with the amount.
 */
export function joinNoteLine(
  task: Pick<ISchedulerTask, 'name' | 'start' | 'end'>,
  qty: number,
  unit: string,
  t: TFunction,
): string {
  return taskNoteLine(
    'logistics.orderDialog.joinTaskNoteNoCad',
    { task: task.name, start: task.start, end: task.end, qty, unit },
    t,
  );
}
